import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  type Observable,
  catchError,
  defer,
  finalize,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  of,
  shareReplay,
  tap,
} from 'rxjs';

import { ApiError } from '@core/api/api-error';
import { buildApiUrl } from '@core/api/api-url';
import { APP_CONFIG } from '@core/app-config';
import { AuthSession, type SessionEndReason } from '@core/auth/auth-session';

export interface AuthTokenResponse {
  readonly code: string;
  readonly accessToken: string;
  /** Also set as an HttpOnly cookie; the web client never stores or sends this value. */
  readonly refreshToken: string;
  readonly expiresInSeconds: number;
  readonly roles: readonly string[];
  readonly isNewUser?: boolean | null;
}

/** App-only roles. Someone holding nothing but these has no business in the back office. */
const CONSUMER_ROLES: ReadonlySet<string> = new Set(['Customer', 'BusinessOwner']);

export function hasBackOfficeAccess(roles: readonly string[]): boolean {
  return roles.some((role) => !CONSUMER_ROLES.has(role));
}

// Shared by every tab of the portal, so two tabs never refresh with the same cookie.
const REFRESH_LOCK = 'search8-auth-refresh';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(AuthSession);
  private readonly router = inject(Router);
  private readonly apiBaseUrl = inject(APP_CONFIG).apiBaseUrl;

  private refreshInFlight: Observable<string> | null = null;

  /**
   * Swaps the refresh cookie for a new access token. The server rotates the cookie on
   * every refresh and treats a reused one as stolen, signing the user out everywhere, so
   * concurrent callers share one request and other tabs wait on a browser lock.
   */
  refresh(): Observable<string> {
    this.refreshInFlight ??= defer(() =>
      from(withLock(REFRESH_LOCK, () => lastValueFrom(this.postRefresh()))),
    ).pipe(
      tap((response) => this.session.start(response.accessToken, response.roles)),
      map((response) => response.accessToken),
      finalize(() => (this.refreshInFlight = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.refreshInFlight;
  }

  /** Called once at startup: the access token lives in memory, so a reload starts without one. */
  restoreSession(): Promise<void> {
    return firstValueFrom(
      this.refresh().pipe(
        map(() => {
          if (!hasBackOfficeAccess(this.session.user()?.roles ?? [])) {
            this.session.clear();
          }
        }),
        catchError((error: unknown) => {
          this.session.clear(isBlocked(error) ? 'blocked' : null);
          return of(undefined);
        }),
      ),
    );
  }

  /** Signs out on the server, then locally, even if the server call fails. */
  logout(): Observable<void> {
    return this.http
      .post<void>(this.url('auth/logout'), { allDevices: false }, { withCredentials: true })
      .pipe(
        catchError(() => of(undefined)),
        map(() => undefined),
        finalize(() => this.session.clear()),
      );
  }

  /** Ends a session the server has rejected and sends the user back to login. */
  endSession(reason: SessionEndReason): void {
    this.session.clear(reason);
    const current = this.router.url;
    if (!current.startsWith('/auth')) {
      void this.router.navigate(['/auth/login'], { queryParams: { returnUrl: current } });
    }
  }

  private postRefresh(): Observable<AuthTokenResponse> {
    return this.http.post<AuthTokenResponse>(
      this.url('auth/token/refresh'),
      {},
      { withCredentials: true },
    );
  }

  private url(path: string): string {
    return buildApiUrl(this.apiBaseUrl, path);
  }
}

export function isBlocked(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'USER_BLOCKED';
}

function withLock<T>(name: string, task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? locks.request(name, task) : task();
}
